import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Input, Label } from "@rakazo/ui-web";
import { useEffect, useState } from "react";
import type { LiveVoiceStatus } from "../../lib/live-voice";
import { loadLiveStatus } from "../../lib/live-voice-rpc";
import { useSpaceRpc } from "../../lib/space-rpc";
import { errorText } from "../../lib/user-error";
import { ConnectionDataDisclosure } from "../ConnectionDataDisclosure";

/**
 * A personal Google key for live voice. It is stored with the person's model
 * credentials, never as a deployment-wide service key, and is used only to mint
 * the ephemeral live token.
 */
export function LiveVoiceKeyCard({
  onBusyChange,
  onSaved,
}: {
  onBusyChange?: (busy: boolean) => void;
  onSaved?: () => void;
} = {}) {
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const [status, setStatus] = useState<LiveVoiceStatus | null | undefined>(undefined);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onBusyChange?.(saving);
    return () => onBusyChange?.(false);
  }, [saving, onBusyChange]);

  async function reload() {
    const next = await loadLiveStatus();
    setStatus(next);
  }

  useEffect(() => {
    let active = true;
    void loadLiveStatus()
      .then((next) => {
        if (active) setStatus(next);
      })
      .catch(() => {
        if (active) setStatus(null);
      });
    return () => {
      active = false;
    };
  }, []);

  if (status === undefined) return null;

  if (status === null || !status.enabled) {
    return (
      <section className="rounded-xl border border-border p-4">
        <h2 className="text-sm font-medium text-foreground">
          <Trans>Live voice</Trans>
        </h2>
        <p className="mt-1 text-[13px] text-muted-foreground">
          <Trans>Live conversation is turned off on this server.</Trans>
        </p>
      </section>
    );
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const key = apiKey.trim();
    if (!key || saving) return;
    setSaving(true);
    setError(null);
    try {
      await rpc.models.connect({
        provider: "google",
        apiKey: key,
        label: "Google",
        // A live-only key must not replace the bot's chat model with a Google default.
        makeDefault: false,
      });
      setApiKey("");
      setReplacing(false);
      await reload();
      onSaved?.();
    } catch (cause) {
      setError(errorText(cause, t`Could not save the Google key`));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-xl border border-border p-4">
      <h2 className="text-sm font-medium text-foreground">
        <Trans>Live voice</Trans>
      </h2>
      <p className="mt-1 text-[13px] leading-[1.5] text-muted-foreground">
        <Trans>
          Add your own Google API key to use live conversation. It is stored on your account, not
          shared with other people, and is not used for your bots’ models.
        </Trans>
      </p>
      {status.hasGoogleKey && !replacing ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <p className="text-[13px] text-success">
            <Trans>Google key connected</Trans>
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => {
              setReplacing(true);
              setError(null);
            }}
          >
            <Trans>Replace key</Trans>
          </Button>
        </div>
      ) : (
        <form onSubmit={save} className="mt-3 flex flex-col gap-2">
          <ConnectionDataDisclosure provider="Google" use="live" />
          <Label htmlFor="live-voice-google-key" className="sr-only">
            <Trans>Google API key</Trans>
          </Label>
          <Input
            id="live-voice-google-key"
            type="password"
            value={apiKey}
            onChange={(event) => {
              setApiKey(event.target.value);
              setError(null);
            }}
            autoComplete="off"
            spellCheck={false}
            placeholder={t`Google API key`}
            className="h-10 rounded-xl"
          />
          <Button type="submit" className="h-10 rounded-xl" disabled={saving || !apiKey.trim()}>
            {saving ? (
              <Trans>Saving…</Trans>
            ) : replacing ? (
              <Trans>Replace key</Trans>
            ) : (
              <Trans>Save key</Trans>
            )}
          </Button>
        </form>
      )}
      {error ? (
        <p role="alert" className="mt-2 text-[13px] text-destructive">
          {error}
        </p>
      ) : null}
    </section>
  );
}
